// src/hooks/useFeedback.ts
//
// Stores up/down feedback for guide cards in /users/{uid}/feedback/.
// Independent from the cards themselves so feedback survives card
// expiry — it's a long-lived signal for future recommendation tuning.
//
// Each feedback doc:
//   {
//     rating: 'up' | 'down',
//     card_kind: 'place' | 'event' | …,
//     card_title: string,
//     card_tags: string[],
//     place_name?: string,
//     for_city?: string,
//     rated_at: Timestamp
//   }
//
// Rules:
//   - Toggling: tapping the active rating clears it (returns to neutral).
//   - Tapping the opposite rating swaps from up→down or down→up.
//   - Thumbs down ALSO dismisses the card from the feed (signal +
//     remove). Thumbs up is non-destructive — the card stays.
//   - Heart (like) is independent — see useGuideFeed.setLiked. Heart
//     means "save indefinitely" which is a different intent from
//     "more like this."

import { useCallback, useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import type { GuideFeedCard } from './useGuideFeed';
import { writeFeedItem } from '../feed/writeFeedItem';

export type Rating = 'up' | 'down' | null;

/** Watch the rating for a single card. Live snapshot. */
export function useCardRating(cardId: string | null | undefined): Rating {
  const [rating, setRating] = useState<Rating>(null);
  useEffect(() => {
    let unsubSnap: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      if (!user || !cardId) {
        setRating(null);
        return;
      }
      const ref = doc(db, 'users', user.uid, 'feedback', cardId);
      unsubSnap = onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            setRating(null);
            return;
          }
          const data = snap.data() as any;
          if (data?.rating === 'up' || data?.rating === 'down') {
            setRating(data.rating);
          } else {
            setRating(null);
          }
        },
        () => setRating(null)
      );
    });
    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, [cardId]);
  return rating;
}

/** Imperative actions to set/toggle ratings. */
export function useFeedbackActions() {
  const setRating = useCallback(
    async (card: GuideFeedCard, next: Rating): Promise<void> => {
      const user = auth.currentUser;
      if (!user || !card?.id) return;
      const ref = doc(db, 'users', user.uid, 'feedback', card.id);

      // Read existing doc first so we can clean up any prior feed
      // bridge if the rating is changing or being cleared.
      let prior: any = null;
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) prior = snap.data();
      } catch {
        /* ignore — non-existence is fine */
      }
      const priorFeedItemId: string | undefined = prior?.feed_item_id;

      try {
        if (next === null) {
          await deleteDoc(ref);
          // Remove any bridged feed_item we created when this was
          // 'up' previously.
          if (priorFeedItemId) {
            try {
              await deleteDoc(doc(db, 'feed_items', priorFeedItemId));
            } catch (err) {
              console.warn('setRating: bridged feed_item cleanup failed:', err);
            }
          }
          return;
        }

        // Build the feedback payload.
        const payload: Record<string, any> = {
          rating: next,
          card_kind: card.kind,
          card_title: card.title,
          card_tags: Array.isArray(card.tags) ? card.tags : [],
          place_name: card.place_name,
          for_city: card.for_city,
          for_country: card.for_country,
          rated_at: serverTimestamp(),
        };

        // ── Feed bridge ──
        // Only thumbs-UP creates a feed item — thumbs-down is private
        // negative signal, doesn't belong on a social feed. If we're
        // moving to 'up' and don't already have a bridged item,
        // create one and stamp the id. If we're moving to 'down'
        // from 'up', clean up the existing bridged item.
        let nextFeedItemId: string | undefined = priorFeedItemId;
        if (next === 'up' && !priorFeedItemId) {
          try {
            // Conditional snapshot — Firestore rejects `undefined`
            // values, so we only include keys with real data.
            const snapshot: Record<string, any> = {
              title: card.title || card.place_name || 'Saved card',
            };
            if (card.body || card.meta) {
              snapshot.caption = card.body || card.meta;
            }
            if (card.photo_url) {
              snapshot.image_path = card.photo_url;
              snapshot.preview_url = card.photo_url;
            }
            // Same location composition as the heart bridge — see
            // useGuideFeed.setLiked for the rationale.
            const placeName =
              card.place_name ||
              (card.for_city && card.for_country
                ? `${card.for_city}, ${card.for_country}`
                : card.for_city || card.for_country) ||
              undefined;
            const hasCoords =
              typeof card.location?.lat === 'number' &&
              typeof card.location?.lng === 'number';
            if (hasCoords || placeName) {
              const loc: Record<string, any> = {};
              if (hasCoords) {
                loc.lat = card.location!.lat;
                loc.lng = card.location!.lng;
              }
              if (placeName) loc.place_name = placeName;
              snapshot.location = loc;
            }
            const newId = await writeFeedItem({
              kind: 'guide_save',
              snapshot,
              source: { kind: 'guide_card', id: card.id },
            });
            if (newId) nextFeedItemId = newId;
          } catch (err) {
            console.warn('setRating: feed bridge create failed:', err);
          }
        } else if (next === 'down' && priorFeedItemId) {
          try {
            await deleteDoc(doc(db, 'feed_items', priorFeedItemId));
            nextFeedItemId = undefined;
          } catch (err) {
            console.warn('setRating: feed bridge cleanup failed:', err);
          }
        }
        if (nextFeedItemId) payload.feed_item_id = nextFeedItemId;

        const clean: Record<string, any> = {};
        for (const [k, v] of Object.entries(payload)) {
          if (v !== undefined) clean[k] = v;
        }
        await setDoc(ref, clean, { merge: true });
      } catch (err) {
        console.warn('useFeedbackActions.setRating failed:', err);
      }
    },
    []
  );

  return { setRating };
}
